import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { UiComponent } from '@df/ui/ui.component';
import { EXIT_COMPONENT_EDIT_EVENT } from '@icms/core/integration/icms-core-integration-interfaces';
import { BehaviorSubject } from 'rxjs';
import { AppBusyStateComponent } from './common/busy-state/app-busy-state.component';
import { RouterAppBusyStateComponent } from './common/busy-state/router-app-busy-state.component';
import { IfNotCheckoutProcessDirective } from './common/if-checkout/if-not-checkout-process.directive';
import { NavigationHeightHoldDirective } from './common/navigation-height-hold/navigation-height-hold.directive';
import { PageFooterComponent } from './page/footer/page-footer.component';
import { PageHeaderComponent } from './page/header/page-header.component';
import { NPageOffCanvas, PageOffCanvasComponent } from './page/off-canvas/page-off-canvas.component';

export const PAGE_HEADER_WRAPPER_ELEMENT = 'page-header-wrapper';

@Component({
    selector: 'app-root',
    templateUrl: './app-root.html',
    styleUrl: './app-root.scss',
    imports: [
        PageOffCanvasComponent,
        AppBusyStateComponent,
        RouterAppBusyStateComponent,
        PageHeaderComponent,
        PageFooterComponent,
        ToggleClassDirective,
        ElementHeightDirective,
        RouterOutlet,
        IfNotCheckoutProcessDirective,
        NavigationHeightHoldDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppRootComponent extends UiComponent implements OnInit {
    protected readonly isAfterContentInit = signal<boolean>(false);
    /**
     * Header element id used withe element height
     */
    protected readonly headerElementId = PAGE_HEADER_WRAPPER_ELEMENT;

    /**
     * If off canvas is visible
     */
    readonly offCanvasState = new BehaviorSubject<NPageOffCanvas.EOpenState>(NPageOffCanvas.EOpenState.closed);

    protected pageHeaderRef = viewChild<PageHeaderComponent>('pageHeader');
    protected pageHeaderRef$ = toObservable(this.pageHeaderRef);

    private readonly app = inject(App);
    private readonly afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private readonly destroyRef = inject(DestroyRef);

    protected hasHeader = this.app.hasSSRCode;

    override ngOnInit() {
        if (!this.app.hasSSRCode) {
            this.afterContentInitDirectiveService.lock(this);
        }

        this.offCanvasState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.app.events.broadcast(EXIT_COMPONENT_EDIT_EVENT);
        });

        this.pageHeaderRef$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
            if (!this.app.hasSSRCode && value) {
                const subs = value.hasContent$.subscribe(hasContent => {
                    if (!this.hasHeader && hasContent) {
                        this.hasHeader = hasContent;
                        this.detectChanges();

                        this.timeoutService.setTimeout(() => {
                            subs.unsubscribe();
                            this.afterContentInitDirectiveService.unlock(this);
                        });
                    }
                });

                const sub2 = this.afterContentInitDirectiveService.ready.subscribe(ready => {
                    if (ready) {
                        this.isAfterContentInit.set(true);
                        this.detectChanges();
                        this.timeoutService.setTimeout(() => {
                            sub2.unsubscribe();
                        });
                    }
                });
            }
        });

        super.ngOnInit();
    }
}
